import React, { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface DailySummary {
  date: string
  cost: number
}

interface CostTimeSeriesChartProps {
  data?: DailySummary[]
  isLoading?: boolean
  error?: string | null
}

/**
 * CostTimeSeriesChart displays the cost over time with hover tooltips
 * X-axis: dates, Y-axis: cost in dollars
 * Uses Puzzle brand color #FF5433 for the line
 */
const CostTimeSeriesChart: React.FC<CostTimeSeriesChartProps> = ({
  data = [],
  isLoading = false,
  error = null,
}) => {
  // Memoize the data to avoid unnecessary rerenders
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []
    return data.slice(-7).map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      cost: parseFloat(item.cost.toFixed(2)),
    }))
  }, [data])

  // Loading state
  if (isLoading) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#F3F3F3] rounded-puzzle"
        role="status"
        aria-label="Loading chart"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#FF5433] border-t-transparent mx-auto mb-2" />
          <p className="text-[#666666] text-sm">Loading chart...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#FFEBEB] rounded-puzzle border border-[#D63329]"
        role="alert"
      >
        <div className="text-center">
          <p className="text-[#D63329] text-sm font-bold">Error loading chart</p>
          <p className="text-[#666666] text-xs mt-1">{error}</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (chartData.length === 0) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center bg-[#F3F3F3] rounded-puzzle"
        role="status"
      >
        <p className="text-[#999999] text-sm">No data available</p>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#DCDCDC" />
          <XAxis
            dataKey="date"
            tick={{ fill: '#666666', fontSize: 12 }}
            axisLine={{ stroke: '#DCDCDC' }}
          />
          <YAxis
            tick={{ fill: '#666666', fontSize: 12 }}
            axisLine={{ stroke: '#DCDCDC' }}
            label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', offset: 10 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #DCDCDC',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            labelStyle={{ color: '#000000' }}
            formatter={(value: unknown) => {
              if (typeof value === 'number') {
                return [`$${value.toFixed(2)}` as React.ReactNode, 'Cost']
              }
              return [`$${value}`, 'Cost']
            }}
          />
          <Legend wrapperStyle={{ paddingTop: '16px' }} />
          <Line
            type="monotone"
            dataKey="cost"
            stroke="#FF5433"
            strokeWidth={2}
            dot={{ fill: '#FF5433', r: 4 }}
            activeDot={{ r: 6 }}
            name="Cost"
            isAnimationActive={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export default CostTimeSeriesChart
